import './MovementLine.scss';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowsH,
  faBars,
  faCube,
  faCutlery,
  faFlask,
  faParking,
  faSnowflake,
  faTrash,
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';

// General MovementLine Component
const MovementLine = ({ movement, movementType }) => {
  const navigate = useNavigate();

  const status =
    movement?.warehouseData?.confirmed != null
      ? movement?.warehouseData?.confirmed
        ? 'complete'
        : 'in_progress'
      : 'not_in_warehouse';

  const assignments = movement?.assignments;

  const jobDetails = assignments[0]?.job;
  const jobReference = jobDetails?.jobReference;
  const haulierName = movement?.supplier?.name || 'N/A';

  const quantity = assignments.map(pkg => pkg.quantity).reduce((a, b) => a + b, 0);
  const type = jobDetails?.packageType?.shortName
    ? assignments.every(
        pkg => pkg.job?.packageType?.shortName == jobDetails?.packageType?.shortName,
      )
      ? jobDetails?.packageType?.shortName
      : 'pkg'
    : 'pkg';

  const weight = assignments.map(pkg => pkg.grossWeightKg).reduce((a, b) => a + b, 0);
  const cubicMetres = assignments
    .map(pkg => Number(pkg.cubicMetres))
    .reduce((a, b) => a + b, 0)
    .toFixed(2);

  const customerName = assignments.every(
    pkg => pkg.job?.customer?.name == jobDetails?.customer?.name,
  )
    ? jobDetails?.customer?.name
    : 'N/A';

  const expectedArrivalTime = movement?.currentExpectedArrivalDate
    ? new Date(movement?.currentExpectedArrivalDate).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    : 'N/A';
  const hazardous = jobDetails?.cargoHazardous;
  const fullLoad = jobDetails?.fullLoad;
  const doNotStack = jobDetails?.doNotStack;
  const foodstuffs = jobDetails?.commodityCategory?.foodstuffs;
  const temperatureControlled = jobDetails?.temperatureControlled;
  const waste = jobDetails?.commodityCategory?.waste;
  const overlength = jobDetails?.overlength;
  const prioritise = movement?.prioritise;

  // Import/export only details
  const groupageReference = movement?.reference;
  const trailerNumber = movement?.vehicleRegistration;
  const trailerType = movement?.trailerType?.name;
  const route = jobDetails?.jobReference.slice(0, 5);
  const jobNumber = assignments
    ?.map(pkg => pkg.job?.jobReference)
    .filter((x, i, a) => a.indexOf(x) == i).length;

  return (
    <div
      className={`movement_line_container ${status}`}
      data-testid="movement_line_container"
      onClick={() => navigate(`/${location.pathname.split('/')[1]}/${movement.id}`)}
    >
      <div className="movement_line_row">
        <div className="movement_line_reference">
          {movementType == 'imports' || movementType == 'exports'
            ? groupageReference || trailerNumber
              ? `${groupageReference || ''} ${trailerNumber ? `(Unit: ${trailerNumber})` : ''}`
              : '(No unit/trailer no.)'
            : jobReference}
        </div>
        <div
          className={`movement_line_haulier ${
            movementType == 'imports' || movementType == 'exports' ? 'bold' : ''
          }`}
        >
          {haulierName}
        </div>
        <div className="movement_line_quantity_type">
          {quantity} {type}
        </div>
        <div className="movement_line_weight">{weight} kg</div>
        <div className="movement_line_cubic_metres">{cubicMetres}m³</div>
      </div>
      <div className="movement_line_row">
        <div className="movement_line_symbol_row">
          {hazardous && (
            <div className="movement_line_symbol hazardous">
              <FontAwesomeIcon icon={faFlask} data-testid="hazard_icon" />
            </div>
          )}
          {temperatureControlled && (
            <div className="movement_line_symbol temp_control">
              <FontAwesomeIcon icon={faSnowflake} data-testid="temperature_icon" />
            </div>
          )}
          {waste && (
            <div className="movement_line_symbol waste">
              <FontAwesomeIcon icon={faTrash} data-testid="waste_icon" />
            </div>
          )}

          {(movementType == 'collections' || movementType == 'deliveries') && (
            <>
              {doNotStack && (
                <div className="movement_line_symbol do_not_stack">
                  <FontAwesomeIcon icon={faBars} data-testid="do_not_stack_icon" />
                </div>
              )}
              {foodstuffs && (
                <div className="movement_line_symbol foodstuffs">
                  <FontAwesomeIcon icon={faCutlery} data-testid="foodstuffs_icon" />
                </div>
              )}
              {overlength && (
                <div className="movement_line_symbol overlength">
                  <FontAwesomeIcon icon={faArrowsH} data-testid="overlength_icon" />
                </div>
              )}
            </>
          )}

          {(movementType == 'imports' || movementType == 'exports') && (
            <>
              {fullLoad && (
                <div className="movement_line_symbol full_load">
                  <FontAwesomeIcon icon={faCube} data-testid="full_load_icon" />
                </div>
              )}
              {prioritise && (
                <div className="movement_line_symbol prioritise">
                  <FontAwesomeIcon icon={faParking} data-testid="prioritise_icon" />
                </div>
              )}
            </>
          )}
        </div>
        {movementType == 'imports' || movementType == 'exports' ? (
          <div className="movement_line_trailer_type">{trailerType}</div>
        ) : (
          <div className="movement_line_customer">{customerName}</div>
        )}
        <div className="movement_line_arrival_time">{expectedArrivalTime}</div>

        {(movementType == 'imports' || movementType == 'exports') && (
          <>
            <div className="movement_line_route">{route}</div>
            <div className="movement_line_job_number">
              {jobNumber} job{jobNumber > 1 ? 's' : ''}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

MovementLine.propTypes = {
  movement: PropTypes.object,
  movementType: PropTypes.string,
};

export default MovementLine;
